import React, { useState, useEffect } from 'react';
import { Badge } from '../common/Badge';
import { prosthesisApi } from '../../services/api';
import { DashboardProthesisStats, CommandeProtheseList, PrestataireStats } from '../../types';
import {
  Package, Clock, AlertTriangle, CheckCircle, TrendingUp,
  Users, Activity, ChevronRight,
} from 'lucide-react';

const statusConfig: Record<string, { label: string; color: string; bg: string; step: number }> = {
  empreintes_prises:       { label: 'Empreintes', color: '#6366f1', bg: '#eef2ff', step: 1 },
  empreintes_envoyees:     { label: 'Envoyées',   color: '#8b5cf6', bg: '#f5f3ff', step: 2 },
  en_fabrication:          { label: 'Fabrication',color: '#0ea5e9', bg: '#f0f9ff', step: 3 },
  fabrication_terminee:    { label: 'Terminée',   color: '#0d9488', bg: '#f0fdfa', step: 4 },
  prothese_livree_cabinet: { label: 'Livrée',     color: '#10b981', bg: '#ecfdf5', step: 5 },
  prothese_posee:          { label: 'Posée',      color: '#22c55e', bg: '#f0fdf4', step: 6 },
  terminee:                { label: 'Terminée',   color: '#64748b', bg: '#f8fafc', step: 7 },
  annulee:                 { label: 'Annulée',    color: '#ef4444', bg: '#fef2f2', step: 0 },
};

const StatusChip: React.FC<{ status: string; display?: string }> = ({ status, display }) => {
  const cfg = statusConfig[status] ?? { label: display ?? status, color: '#9ca3af', bg: '#f9fafb', step: 0 };
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, background: cfg.bg }}
    >
      {display ?? cfg.label}
    </span>
  );
};

const KpiCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  accent: string;
}> = ({ icon, label, value, sub, accent }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: `${accent}18` }}>
      <div style={{ color: accent }}>{icon}</div>
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold mt-0.5" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const OrderCard: React.FC<{ order: CommandeProtheseList; accent?: string }> = ({ order, accent = '#0ea5e9' }) => (
  <div className="flex items-start justify-between p-4 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all">
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-bold text-gray-800">{order.numero_commande}</span>
        <StatusChip status={order.status} display={order.status_display} />
        {order.is_en_retard && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600">
            Retard
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600 truncate">{order.patient_nom} {order.patient_prenoms}</p>
      <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
        <Clock className="w-3 h-3" />
        Prév. {new Date(order.date_livraison_prevue).toLocaleDateString('fr-FR')}
      </p>
    </div>
    <div className="text-right ml-3 flex-shrink-0">
      <p className="text-xs text-gray-400">{order.prestataire_nom}</p>
    </div>
  </div>
);

const ProsthesisDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardProthesisStats | null>(null);
  const [commandesRetard, setCommandesRetard] = useState<CommandeProtheseList[]>([]);
  const [livraisonsSemaine, setLivraisonsSemaine] = useState<CommandeProtheseList[]>([]);
  const [prestataireStats, setPrestataireStats] = useState<PrestataireStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      prosthesisApi.getDashboardStats(),
      prosthesisApi.getCommandesEnRetard(),
      prosthesisApi.getLivraisonsSemaine(),
      prosthesisApi.getPrestataireStats(),
    ]).then(([s, r, l, p]) => {
      setStats(s.data);
      setCommandesRetard(r.data);
      setLivraisonsSemaine(l.data);
      setPrestataireStats(p.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-400">Chargement…</span>
      </div>
    </div>
  );

  const prestatairesActifs = prestataireStats.filter(p => p.prestataire.is_active).length;

  return (
    <div className="space-y-6">

      {/* ── KPI Row ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard icon={<Package className="w-5 h-5" />} label="Total commandes"   value={stats?.total_commandes ?? 0}   accent="#6366f1" />
        <KpiCard icon={<Activity className="w-5 h-5" />} label="En cours"          value={stats?.commandes_actives ?? 0}  accent="#0ea5e9" sub="commandes actives" />
        <KpiCard icon={<AlertTriangle className="w-5 h-5" />} label="En retard"    value={stats?.commandes_retard ?? 0}   accent="#ef4444" sub="à traiter en priorité" />
        <KpiCard icon={<Users className="w-5 h-5" />}    label="Prestataires actifs" value={prestatairesActifs}           accent="#0d9488" />
      </div>

      {/* ── Répartition par statut ── */}
      {stats && stats.repartition_status.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gray-400" />
            Pipeline de fabrication
          </h3>
          <div className="flex flex-wrap gap-3">
            {stats.repartition_status
              .filter(item => item.status !== 'terminee' && item.status !== 'annulee')
              .sort((a, b) => (statusConfig[a.status]?.step ?? 0) - (statusConfig[b.status]?.step ?? 0))
              .map((item, i, arr) => {
                const cfg = statusConfig[item.status];
                return (
                  <React.Fragment key={item.status}>
                    <div className="flex flex-col items-center">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-bold shadow-sm"
                        style={{ background: cfg?.bg ?? '#f9fafb', color: cfg?.color ?? '#9ca3af' }}
                      >
                        {item.count}
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5 text-center max-w-[64px] leading-tight">
                        {cfg?.label ?? item.status}
                      </p>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="flex items-center mt-0 self-start pt-4">
                        <ChevronRight className="w-4 h-4 text-gray-200" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            {/* Terminées + Annulées à part */}
            <div className="flex-1 min-w-0" />
            {['terminee','annulee'].map(s => {
              const item = stats.repartition_status.find(x => x.status === s);
              if (!item) return null;
              const cfg = statusConfig[s];
              return (
                <div key={s} className="flex flex-col items-center">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-bold"
                    style={{ background: cfg.bg, color: cfg.color }}
                  >
                    {item.count}
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5 text-center">{cfg.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Retards + Livraisons ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-red-50 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-500" />
            </div>
            <h3 className="text-sm font-semibold text-gray-700">
              Commandes en retard
              <span className="ml-2 text-xs font-normal text-red-400">({commandesRetard.length})</span>
            </h3>
          </div>
          <div className="space-y-2">
            {commandesRetard.length === 0 ? (
              <div className="text-center py-8 text-gray-300 text-sm flex flex-col items-center gap-2">
                <CheckCircle className="w-8 h-8 text-green-200" />
                Aucune commande en retard
              </div>
            ) : (
              commandesRetard.slice(0, 5).map(o => <OrderCard key={o.id} order={o} accent="#ef4444" />)
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-teal-50 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-teal-500" />
            </div>
            <h3 className="text-sm font-semibold text-gray-700">
              Livraisons cette semaine
              <span className="ml-2 text-xs font-normal text-teal-400">({livraisonsSemaine.length})</span>
            </h3>
          </div>
          <div className="space-y-2">
            {livraisonsSemaine.length === 0 ? (
              <div className="text-center py-8 text-gray-300 text-sm">Aucune livraison prévue cette semaine</div>
            ) : (
              livraisonsSemaine.slice(0, 5).map(o => <OrderCard key={o.id} order={o} accent="#0d9488" />)
            )}
          </div>
        </div>
      </div>

      {/* ── Prestataires ── */}
      {prestataireStats.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            Performance des prestataires
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Prestataire</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Total</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">En cours</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Retard</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Délai moy.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {prestataireStats.map(stat => (
                  <tr key={stat.prestataire.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {stat.prestataire.nom.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{stat.prestataire.nom}</p>
                          {stat.prestataire.contact_principal && (
                            <p className="text-xs text-gray-400">{stat.prestataire.contact_principal}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center font-semibold text-gray-700">{stat.commandes_total}</td>
                    <td className="py-3 px-3 text-center">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-50 text-blue-600 font-semibold text-xs">
                        {stat.commandes_actives}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {stat.commandes_retard > 0 ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-red-50 text-red-600 font-semibold text-xs">
                          {stat.commandes_retard}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center text-gray-600 text-xs">
                      {stat.prestataire.delai_fabrication_moyen}j
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProsthesisDashboard;
